import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";
import { withTranslation } from "react-i18next";
import Text from "components/Text";
import addEventStyles from "accountOwner/pages/AddEvent/AddEvent.module.scss";
import crossIcon from "assets/images/cross.svg";
import StaffDefaultImage from "assets/images/staff-default-rounded.png";
import { getOfficesByOwnerAndUser } from "repositories/office-repository";
import Empty from "components/Empty";

/**
 * Office picker for drag-drop assignment when a staff member belongs to multiple offices.
 */
function SelectOfficeForUserModal({
  t,
  isOpen,
  ownerId,
  userId,
  isShift = true,
  onClose,
  onConfirm,
}) {
  const [search, setSearch] = useState("");
  const [officeList, setOfficeList] = useState([]);
  const [allOffices, setAllOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  useEffect(() => {
    if (!isOpen || !ownerId || !userId) return;

    let isMounted = true;
    setIsLoading(true);
    setSearch("");
    setSelectedOffice(null);

    getOfficesByOwnerAndUser(ownerId, userId)
      .then((offices) => {
        if (!isMounted) return;
        // No choice to make: assign the sole office without keeping the picker open.
        if (Array.isArray(offices) && offices.length === 1 && offices[0]?.id) {
          onConfirmRef.current?.(offices[0]);
          return;
        }
        setAllOffices(offices);
        setOfficeList(offices);
      })
      .catch(() => {
        if (!isMounted) return;
        setAllOffices([]);
        setOfficeList([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, ownerId, userId]);

  const filteredOffices = useMemo(() => {
    const value = search.trim().replace(/\s+/g, "").toLowerCase();
    if (!value) return officeList;
    return officeList.filter((office) =>
      office?.name?.replace(/\s+/g, "").toLowerCase().includes(value),
    );
  }, [officeList, search]);

  const handleSearch = (value) => {
    setSearch(value);
    const trimmed = value.trim().replace(/\s+/g, "").toLowerCase();
    if (!trimmed) {
      setOfficeList(allOffices);
      return;
    }
    setOfficeList(
      allOffices.filter((office) =>
        office?.name?.replace(/\s+/g, "").toLowerCase().includes(trimmed),
      ),
    );
  };

  const handleConfirm = () => {
    if (!selectedOffice?.id) return;
    onConfirm(selectedOffice);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      className="modal-dialog-centered modal-width-660 new-group-modal"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={onClose}>
        <img src={crossIcon} alt="close" />
      </span>

      <ModalBody>
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          {t(
            isShift
              ? "accountOwner.selectOfficeForShift"
              : "scheduler.selectOfficeForEvent",
          )}
        </Text>

        {isLoading && (
          <div className="loader-container">
            <Spinner animation="border" className="loader-spinner" />
          </div>
        )}
        <div className={"search-box w-100 " + addEventStyles["search-box"]}>
          <input
            type="text"
            placeholder={t("accountOwner.searchByName")}
            value={search}
            onChange={(e) => handleSearch(e.currentTarget.value)}
          />
          <span className="ico">
            <img
              src={require("assets/images/search-icon.svg").default}
              alt="icon"
            />
          </span>
        </div>

        <Text
          size="12px"
          marginBottom="0px"
          marginTop="20px"
          weight="400"
          color="rgb(111, 119, 136)"
        >
          {selectedOffice ? ` ${1} ${t("Selected")}` : ""}
        </Text>
        {filteredOffices.length > 0 ? (
          <ul
            className={"modal-employee-list group-list radio-settings"}
            id="scrollableDiv"
          >
            {filteredOffices.map((item) => (
              <li key={item.id}>
                <div className={`${addEventStyles.border_list} ch-radio`}>
                  <label>
                    <input
                      type="radio"
                      checked={selectedOffice?.id === item.id}
                      onChange={() => setSelectedOffice(item)}
                    />
                    <span className="d-flex align-items-center select-office-radio">
                      <img
                        src={item?.officeImage || StaffDefaultImage}
                        alt="profile-pic"
                        className={addEventStyles.Default_staff_image}
                      />
                      <p className="m-0">{item?.name}</p>
                    </span>
                  </label>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          !isLoading && <Empty Message={t("accountOwner.noOfficeFound")} />
        )}

        <button
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100 mt-4"
          title={t("staff.select")}
          disabled={!selectedOffice?.id}
          onClick={handleConfirm}
        >
          {t("staff.select")}
        </button>
        <button
          className="button button-round button-border btn-mobile-link button-dark mb-md-3 mt-md-4"
          onClick={onClose}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(SelectOfficeForUserModal);
